import { humanErrorMessage } from '~/lib/api-client';
import { filenameFromContentDisposition, bareName } from '~/lib/content-disposition';
import { saveBlob } from '~/features/home/briefing-utils';

/**
 * FRONT-2024: generalized out of `~/features/sketches/sketch-download.ts`
 * (FRONT-1809) so any single-document Eddy App — Slides (decks) first, and
 * eventually Sketches themselves once FRONT-2025 moves them onto the Apps
 * store — can offer the same "download as HTML" / "Save as PDF" pair
 * without a second, hand-copied implementation.
 *
 * Download a served app's HTML as a real file, or open its dedicated print
 * page (the doorway to "Save as PDF" via the browser's own print sheet).
 * Backend contract (`src/apps/routes.ts`, built and verified separately —
 * nothing under `src/` changes here):
 *   - `GET <path>/download` → the app's raw `index.html` (no SDK/metadata
 *     injection), with `Content-Disposition: attachment` and a filename
 *     derived from the title (ASCII `filename=` plus RFC 5987 `filename*`).
 *   - `GET <path>/print` → the same raw HTML with a small print-trigger
 *     script appended.
 *
 * `path` is always an app's own `/apps/<slug>/` (`DeckWire.path`, and
 * eventually `SketchWire.path`) — the two functions below are the SINGLE
 * source of the two addresses, so a gallery card, a chat card, and this
 * module's own browser spec can never build a different URL for the same
 * app. Both tolerate a trailing slash (or its absence).
 */

/**
 * The exact shape the backend's own serve route matches
 * (`/^\/apps\/([a-z0-9][a-z0-9-]*)\/?$/` in `src/apps/routes.ts`). Anything
 * else is not an app address and never becomes one here.
 *
 * FRONT-1809 security review (carried over unchanged): `openAppPrint` hands
 * its result to `window.open`, and a `javascript:` or `data:` URL there
 * executes in THIS origin — a real cross-site-scripting primitive if the
 * value ever stops being server-built. Today it always is (the gallery's
 * wire shape and the publishing tool's own result), so this is defence in
 * depth rather than a live hole; the point is that it stays closed if a
 * future caller passes something looser. Both builders validate, so no
 * caller can opt out by picking the other one.
 */
const APP_PATH_PATTERN = /^\/apps\/[a-z0-9][a-z0-9-]*$/;

/** Thrown for a path that is not an app address. The message is the one the
 *  user sees in the failure toast, so it says what happened in their terms
 *  rather than naming a pattern. */
export class NotAnAppPathError extends Error {
  constructor() {
    super("That app's address doesn't look right, so EDDY didn't open it.");
    this.name = 'NotAnAppPathError';
  }
}

function appBasePath(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  if (!APP_PATH_PATTERN.test(trimmed)) throw new NotAnAppPathError();
  return trimmed;
}

export function appDownloadPath(path: string): string {
  return `${appBasePath(path)}/download`;
}

export function appPrintPath(path: string): string {
  return `${appBasePath(path)}/print`;
}

/**
 * Fallback name when the response carries no usable `Content-Disposition`
 * header. `fallbackTitle` is the app's own (often model-authored) title, so
 * it runs through the SAME `bareName` sanitizer every server-supplied name
 * already goes through (FRONT-1809 adversarial review, finding 10) — a raw
 * title could otherwise carry a path separator or be nothing but dots, and
 * the point of sanitizing a server name is defeated if the client-built
 * fallback skips it. `fallbackWord` is the generic noun to fall back to
 * when the title itself has nothing usable ("app", "deck", "sketch", …) —
 * callers choose their own so the saved file reads right for what it is.
 */
function fallbackFilename(title: string, fallbackWord: string): string {
  return `${bareName(title) ?? fallbackWord}.html`;
}

/**
 * Fetches an app's own HTML and saves it as a file. Deliberately not a
 * plain `<a download>`: if the caller's session has expired, `GET
 * <path>/download` 302s to the sign-in page and `fetch` follows that
 * silently — without checking `redirected`, the browser would save the
 * sign-in page's HTML as the user's "app" with no error at all. This is the
 * first place that redirect check is needed: the app serve routes are
 * registered `authenticated: false` and own their auth via a login bounce
 * (`src/apps/routes.ts`), unlike `/api/okf/export` (which
 * `useExportOkfBundle`, `~/hooks/mutations/use-okf-bundle.ts`, downloads) —
 * that route is an ordinary authenticated JSON route, so an expired session
 * there 401s rather than redirecting, and a plain `!res.ok` check already
 * catches it.
 */
export async function downloadAppHtml(path: string, fallbackTitle: string, fallbackWord: string): Promise<void> {
  const res = await fetch(appDownloadPath(path), { credentials: 'include' });
  if (res.redirected) {
    throw new Error('Your session has expired — sign in again to download this.');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(humanErrorMessage(res.status, text));
  }
  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers.get('content-disposition')) ??
    fallbackFilename(fallbackTitle, fallbackWord);
  saveBlob(blob, filename);
}

/**
 * Opens an app's print page in a new tab — the "Save as PDF" action. The
 * served page carries its own print-trigger script, so the browser's print
 * sheet appears on its own once that tab loads.
 */
export function openAppPrint(path: string): void {
  // `appPrintPath` throws on anything that is not an app address, so
  // `window.open` only ever receives a same-origin path this app built.
  window.open(appPrintPath(path), '_blank', 'noopener,noreferrer');
}
